import React, { useState } from 'react';
import { useSiteContent } from '../context/SiteContentContext';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import emailjs from '@emailjs/browser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ContactForm = () => {
  const { content } = useSiteContent();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const serviceTypes = content.service_types || [];

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const sendEmailJS = async () => {
    // Check if EmailJS is configured
    if (!content.emailjs_service_id || !content.emailjs_template_id || !content.emailjs_public_key) {
      console.log('EmailJS not configured');
      return false;
    }

    try {
      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone || 'Ni podano',
        service: formData.service,
        message: formData.message,
        to_email: content.notification_email || content.email
      };

      await emailjs.send(
        content.emailjs_service_id,
        content.emailjs_template_id,
        templateParams,
        content.emailjs_public_key
      );
      
      console.log('EmailJS sent successfully');
      return true;
    } catch (err) {
      console.error('EmailJS error:', err);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      // Save to database (and try SMTP if configured)
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        message: formData.message
      });
      
      // Also try EmailJS if configured
      if (content.email_method === 'emailjs') {
        await sendEmailJS();
      }
      
      setSubmitted(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', service: '', message: '' });
      
      setTimeout(() => setSubmitted(false), 10000);
    } catch (err) {
      console.error('Error submitting form:', err);
      setError('Prišlo je do napake. Prosimo, poskusite znova ali nas kontaktirajte po telefonu.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="povprasevanje" className="py-20 md:py-28 bg-gray-50">
      <div className="max-w-3xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-[#2D4A5E] mb-4">
            {content.contact_title?.split(' ')[0]} <span className="text-[#4CAF50]">{content.contact_title?.split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="text-gray-600">
            {content.contact_subtitle}
          </p>
        </div>

        {submitted ? (
          <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-xl font-semibold text-green-800 mb-2">Hvala za vaše povpraševanje!</h3>
            <p className="text-green-600 mb-4">Odgovorili vam bomo v najkrajšem možnem času.</p>
            <p className="text-gray-500 text-sm">Lahko nas kontaktirate tudi direktno:</p>
            <p className="text-[#2D4A5E] font-semibold">{content.phone_numbers?.[0]?.number || content.phone}</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6 bg-white p-8 rounded-xl shadow-lg">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ime in priimek <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Ime"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all"
                />
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Priimek"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email <span className="text-red-500">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                placeholder="vas.email@primer.com"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Telefon
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+386 XX XXX XXX"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Vrsta storitve <span className="text-red-500">*</span>
              </label>
              <select
                name="service"
                value={formData.service}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all bg-white cursor-pointer"
              >
                <option value="">Izberite storitev</option>
                {serviceTypes.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Sporočilo <span className="text-red-500">*</span>
              </label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                placeholder="Opišite vaš projekt ali vprašanje..."
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#4CAF50] focus:border-transparent outline-none transition-all resize-vertical"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#4CAF50] hover:bg-[#45a049] text-white font-semibold px-8 py-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Pošiljanje...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Pošlji povpraševanje
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </section>
  );
};

export default ContactForm;
